import { defineStore } from 'pinia'
import { RfrentielsApi, Configuration } from '../api'
import type { DomainesDto, ItemDto, SousDomainesDto } from '../api/models'

// Configuration de l'API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.callaw.avoloi.com'
const apiConfig = new Configuration({
  basePath: API_BASE_URL,
})

// Types pour les suggestions
interface Suggestion {
  id: number
  nom: string
  type: 'domaine' | 'sousDomaine'
}

// Type pour les langues (en attendant le modèle API)
interface Langue {
  id: number
  nom: string
  code?: string
}

// Type pour les barreaux
interface Barreau {
  id: number
  nom: string
  codePostal?: string
}

// Type pour l'état du store
interface ReferentielsState {
  domaines: DomainesDto[]
  sousDomaines: SousDomainesDto[]
  langues: ItemDto[]
  barreaux: ItemDto[]
  loading: boolean
  error: string | null
}

export const useReferentielsStore = defineStore('referentiels', {
  state: (): ReferentielsState => ({
    domaines: [],
    sousDomaines: [],
    langues: [],
    barreaux: [],
    loading: false,
    error: null
  }),

  getters: {
    // Combinaison des domaines et sous-domaines pour les suggestions
    suggestions: (state): Suggestion[] => {
      const domainesSuggestions = state.domaines
        .filter(d => d && d.name) // Protection contre les valeurs undefined
        .map(d => ({
          id: d.id!,
          nom: d.name!,
          type: 'domaine' as const
        }))

      const sousDomainesSuggestions = state.sousDomaines
        .filter(sd => sd && sd.name) // Protection contre les valeurs undefined
        .map(sd => ({
          id: sd.id!,
          nom: sd.name!,
          type: 'sousDomaine' as const
        }))

      return [...domainesSuggestions, ...sousDomainesSuggestions]
    },

    // Filtrer les suggestions par terme de recherche
    filteredSuggestions() {
      return (searchTerm: string): Suggestion[] => {
        if (!searchTerm) return []

        const term = searchTerm.toLowerCase()
        return this.suggestions.filter(suggestion =>
          suggestion && suggestion.nom && suggestion.nom.toLowerCase().includes(term)
        )
      }
    }
  },

  actions: {
    // Charger les domaines de compétence
    async loadDomaines(): Promise<void> {
      if (this.domaines.length > 0) return // Déjà chargé

      this.loading = true
      this.error = null

      try {
        const api = new RfrentielsApi(apiConfig)
        const response = await api.referentielsControllerGetDomains()
        this.domaines = response.data
      } catch (error) {
        console.error('Erreur lors du chargement des domaines:', error)
        this.error = 'Erreur lors du chargement des domaines'
      } finally {
        this.loading = false
      }
    },

    // Charger les sous-domaines
    async loadSousDomaines(): Promise<void> {
      if (this.sousDomaines.length > 0) return // Déjà chargé

      this.loading = true
      this.error = null

      try {
        const api = new RfrentielsApi(apiConfig)
        const response = await api.referentielsControllerGetSousDomaines()
        this.sousDomaines = response.data
      } catch (error) {
        console.error('Erreur lors du chargement des sous-domaines:', error)
        this.error = 'Erreur lors du chargement des sous-domaines'
      } finally {
        this.loading = false
      }
    },

    // Charger les langues
    async loadLangues(): Promise<void> {
      if (this.langues.length > 0) return // Déjà chargé

      this.loading = true
      this.error = null

      try {
        const api = new RfrentielsApi(apiConfig)
        const response = await api.referentielsControllerGetLangues()
        this.langues = response.data as ItemDto[]
      } catch (error) {
        console.error('Erreur lors du chargement des langues:', error)
        this.error = 'Erreur lors du chargement des langues'
      } finally {
        this.loading = false
      }
    },

    // Charger les barreaux par code postal
    async loadBarreaux(codePostal?: string): Promise<void> {
      this.loading = true
      this.error = null

      try {
        const api = new RfrentielsApi(apiConfig)
        const response = await api.referentielsControllerGetBars(codePostal)
        this.barreaux = response.data as Barreau[]
      } catch (error) {
        console.error('Erreur lors du chargement des barreaux:', error)
        this.error = 'Erreur lors du chargement des barreaux'
      } finally {
        this.loading = false
      }
    },

    // Charger tous les référentiels en une fois
    async loadAll(): Promise<void> {
      await Promise.all([
        this.loadDomaines(),
        this.loadSousDomaines(),
        this.loadLangues()
      ])
    }
  }
}) 